import { createAction } from '@reduxjs/toolkit';
import * as Sentry from '@sentry/react';
import forge from 'apis/forge';
import { ChatMode, ChatStatus, DomElement, UserChatEntry } from 'dragonmouse-shared/interfaces';
import { Style } from 'dragonmouse-shared/style';
import {
	selectActiveDsChat,
	selectActiveFrameChat,
	selectActiveSceneChat,
} from 'redux/db/reducers';
import { setActiveFrameChatId } from 'redux/mode/actions';
import store from 'redux/store';

// User-action queueing for chats.
//
// Multiple buttons trigger work that posts to a chat (Approve a plan, Generate
// gallery, Choice, Follow-up, Reconcile, Autofix, etc.). When a click happens
// while the agent is mid-response, two writers race on chat.entries. The
// backend has a 409 safety net; this layer is the studio-side primary defense:
// queue the click, fire it when the chat goes idle.
//
// Architecture:
//   - Three queues (one per chat type), keyed by chatId, ordered by createdAt.
//   - Buttons enqueue via small hooks (useDsChatQueue / useSceneChatQueue /
//     useFrameChatQueue).
//     They never call forge directly.
//   - A module-level singleton (ChatActionQueueExecutor) subscribes to the
//     Redux store. When any chat with a queued head transitions to idle, it
//     fires the head and pops it. One in-flight call per chatId at a time.
//
// Lifecycle: the executor lives outside React, instantiated on module import
// from index.tsx. It survives every view change (panel toggle, prototype
// mode, modal close, navigation) so queued actions never get orphaned.

//----------------------------------------------------------------------
// TYPES
//----------------------------------------------------------------------

export type DsChatActionPayload =
	| { kind: 'approvePlan'; planId: string }
	| { kind: 'addUserEntry'; entry: UserChatEntry; chatMode: ChatMode };

export type SceneChatActionPayload = {
	kind: 'addUserEntry';
	entry: UserChatEntry;
	chatMode: ChatMode;
	style: Style | null;
	mode?: string;
};

export type DsChatActionEntry = {
	id: string;
	chatId: string;
	label: string;
	clientKey?: string;
	createdAt: number;
	firing?: boolean;
	payload: DsChatActionPayload;
};

export type SceneChatActionEntry = {
	id: string;
	chatId: string;
	label: string;
	clientKey?: string;
	createdAt: number;
	firing?: boolean;
	payload: SceneChatActionPayload;
};

export type FrameChatActionPayload = {
	kind: 'addUserEntry';
	entry: UserChatEntry;
	frameId: string;
	selectedDomElements?: DomElement[];
};

export type FrameChatActionEntry = {
	id: string;
	chatId: string;
	label: string;
	clientKey?: string;
	createdAt: number;
	firing?: boolean;
	payload: FrameChatActionPayload;
};

//----------------------------------------------------------------------
// ACTIONS
//----------------------------------------------------------------------

export const enqueueDsChatAction = createAction<DsChatActionEntry>('ENQUEUE_DS_CHAT_ACTION');
export const markDsChatActionFiring = createAction<{ id: string }>('MARK_DS_CHAT_ACTION_FIRING');
export const cancelDsChatAction = createAction<{ id: string }>('CANCEL_DS_CHAT_ACTION');
export const popDsChatAction = createAction<{ id: string }>('POP_DS_CHAT_ACTION');

export const enqueueSceneChatAction = createAction<SceneChatActionEntry>(
	'ENQUEUE_SCENE_CHAT_ACTION'
);
export const markSceneChatActionFiring = createAction<{ id: string }>(
	'MARK_SCENE_CHAT_ACTION_FIRING'
);
export const cancelSceneChatAction = createAction<{ id: string }>('CANCEL_SCENE_CHAT_ACTION');
export const popSceneChatAction = createAction<{ id: string }>('POP_SCENE_CHAT_ACTION');

export const enqueueFrameChatAction = createAction<FrameChatActionEntry>(
	'ENQUEUE_FRAME_CHAT_ACTION'
);
export const markFrameChatActionFiring = createAction<{ id: string }>(
	'MARK_FRAME_CHAT_ACTION_FIRING'
);
export const cancelFrameChatAction = createAction<{ id: string }>('CANCEL_FRAME_CHAT_ACTION');
export const popFrameChatAction = createAction<{ id: string }>('POP_FRAME_CHAT_ACTION');

//----------------------------------------------------------------------
// EXECUTOR
//----------------------------------------------------------------------

const isBusy = (status: ChatStatus | undefined): boolean =>
	status === ChatStatus.PROCESSING || status === ChatStatus.STOPPABLE;

// After a successful fire, hold the per-chat lock + queue entry until the
// server-side state catches up to Redux (chat status flips to busy via
// polling). Without this, a second enqueued fire could race the first one
// across the polling window and 409. If polling never brings the new state
// (e.g. user navigated away and the stream paused), this timeout force-
// releases so the queue doesn't deadlock.
const ACK_TIMEOUT_MS = 5000;

// One per fired-but-not-yet-acknowledged entry.
type PendingAck = {
	entryId: string;
	chatId: string;
	firedAt: number;
};

export class ChatActionQueueExecutor {
	// Locks: chatIds we have an in-flight or pending-acknowledgment fire for.
	// Same chat won't fire its next queue head until its lock clears.
	private firingDsChats = new Set<string>();
	private firingSceneChats = new Set<string>();
	private firingFrameChats = new Set<string>();

	// Entries fired successfully but waiting for polling to acknowledge the
	// resulting status flip. When acknowledged (or timeout), we pop and
	// release the chat's lock.
	private pendingDsAcks = new Map<string, PendingAck>(); // chatId -> ack
	private pendingSceneAcks = new Map<string, PendingAck>();
	private pendingFrameAcks = new Map<string, PendingAck>();

	constructor() {
		store.subscribe(() => this.tick());
	}

	private tick() {
		const state = store.getState();
		this.processAcks(state);
		this.processDsQueue(state);
		this.processSceneQueue(state);
		this.processFrameQueue(state);
	}

	// Resolves any pending acks whose server state has caught up (status went
	// busy) or whose timeout has elapsed. Pops the entry and releases the lock
	// so the next queued head can fire.
	private processAcks(state: ReturnType<typeof store.getState>) {
		const now = Date.now();

		for (const [chatId, ack] of this.pendingDsAcks) {
			const chat = selectActiveDsChat(state.db, chatId);
			const acknowledged = chat ? isBusy(chat.metadata?.status) : false;
			const timedOut = now - ack.firedAt > ACK_TIMEOUT_MS;
			if (acknowledged || timedOut) {
				this.pendingDsAcks.delete(chatId);
				this.firingDsChats.delete(chatId);
				store.dispatch(popDsChatAction({ id: ack.entryId }));
			}
		}

		for (const [chatId, ack] of this.pendingSceneAcks) {
			const chat = selectActiveSceneChat(state.db, chatId);
			const acknowledged = chat ? isBusy(chat.metadata?.status) : false;
			const timedOut = now - ack.firedAt > ACK_TIMEOUT_MS;
			if (acknowledged || timedOut) {
				this.pendingSceneAcks.delete(chatId);
				this.firingSceneChats.delete(chatId);
				store.dispatch(popSceneChatAction({ id: ack.entryId }));
			}
		}

		for (const [chatId, ack] of this.pendingFrameAcks) {
			const chat = selectActiveFrameChat(state.db);
			const acknowledged = chat?.id === chatId ? isBusy(chat.metadata?.status) : false;
			const timedOut = now - ack.firedAt > ACK_TIMEOUT_MS;
			if (acknowledged || timedOut) {
				this.pendingFrameAcks.delete(chatId);
				this.firingFrameChats.delete(chatId);
				store.dispatch(popFrameChatAction({ id: ack.entryId }));
			}
		}
	}

	private processDsQueue(state: ReturnType<typeof store.getState>) {
		const queue = state.mode.dsChatActionQueue;
		if (queue.length === 0) return;

		// Find the FIFO head per chatId. queue is already in createdAt order.
		const heads = new Map<string, DsChatActionEntry>();
		for (const entry of queue) {
			if (!heads.has(entry.chatId)) heads.set(entry.chatId, entry);
		}

		for (const [chatId, entry] of heads) {
			if (this.firingDsChats.has(chatId)) continue;
			const chat = selectActiveDsChat(state.db, chatId);
			if (!chat) continue;
			if (isBusy(chat.metadata?.status)) continue;
			this.fireDs(entry);
		}
	}

	private processSceneQueue(state: ReturnType<typeof store.getState>) {
		const queue = state.mode.sceneChatActionQueue;
		if (queue.length === 0) return;

		const heads = new Map<string, SceneChatActionEntry>();
		for (const entry of queue) {
			if (!heads.has(entry.chatId)) heads.set(entry.chatId, entry);
		}

		for (const [chatId, entry] of heads) {
			if (this.firingSceneChats.has(chatId)) continue;
			const chat = selectActiveSceneChat(state.db, chatId);
			if (!chat) continue;
			if (isBusy(chat.metadata?.status)) continue;
			this.fireScene(entry);
		}
	}

	private async fireDs(entry: DsChatActionEntry) {
		this.firingDsChats.add(entry.chatId);
		store.dispatch(markDsChatActionFiring({ id: entry.id }));
		try {
			switch (entry.payload.kind) {
				case 'approvePlan':
					await forge.approveDsChatPlan.withToast()({
						dsChatId: entry.chatId,
						planId: entry.payload.planId,
					});
					break;
				case 'addUserEntry':
					await forge.dsChatAddUserEntry.withToast()({
						dsChatId: entry.chatId,
						entry: entry.payload.entry,
						chatMode: entry.payload.chatMode,
					});
					break;
			}
			// Successful fire — wait for polling to acknowledge the new busy
			// state before popping + releasing the lock. Keeps the queue
			// indicator showing the entry until the chat visibly moves to
			// PROCESSING, and prevents a same-tick second fire from racing.
			this.pendingDsAcks.set(entry.chatId, {
				entryId: entry.id,
				chatId: entry.chatId,
				firedAt: Date.now(),
			});
			// Schedule a tick after the timeout so processAcks runs even if no
			// other dispatch happens in the meantime.
			setTimeout(() => this.tick(), ACK_TIMEOUT_MS + 50);
		} catch (error) {
			// .withToast() / handleErrorCode already surfaced the error to the
			// user. Capture in Sentry for production debugging — these are the
			// failures that would otherwise be invisible to us. Drop the
			// entry; failed fire is not retried.
			Sentry.captureException(error);
			this.firingDsChats.delete(entry.chatId);
			store.dispatch(popDsChatAction({ id: entry.id }));
		}
	}

	private async fireScene(entry: SceneChatActionEntry) {
		this.firingSceneChats.add(entry.chatId);
		store.dispatch(markSceneChatActionFiring({ id: entry.id }));
		try {
			await forge.addSceneChatUserEntry.withToast()({
				chatId: entry.chatId,
				entry: entry.payload.entry,
				chatMode: entry.payload.chatMode,
				style: entry.payload.style,
				...(entry.payload.mode != null && { mode: entry.payload.mode }),
			});
			this.pendingSceneAcks.set(entry.chatId, {
				entryId: entry.id,
				chatId: entry.chatId,
				firedAt: Date.now(),
			});
			setTimeout(() => this.tick(), ACK_TIMEOUT_MS + 50);
		} catch (error) {
			// see fireDs comment
			Sentry.captureException(error);
			this.firingSceneChats.delete(entry.chatId);
			store.dispatch(popSceneChatAction({ id: entry.id }));
		}
	}

	private processFrameQueue(state: ReturnType<typeof store.getState>) {
		const queue = state.mode.frameChatActionQueue;
		if (queue.length === 0) return;

		// Frame chat is singleton — only one active at a time. Group by chatId
		// for consistency with ds/scene queues.
		const heads = new Map<string, FrameChatActionEntry>();
		for (const entry of queue) {
			if (!heads.has(entry.chatId)) heads.set(entry.chatId, entry);
		}

		for (const [chatId, entry] of heads) {
			if (this.firingFrameChats.has(chatId)) continue;
			const chat = selectActiveFrameChat(state.db);
			if (!chat || chat.id !== chatId) continue;
			if (isBusy(chat.metadata?.status)) continue;
			this.fireFrame(entry);
		}
	}

	private async fireFrame(entry: FrameChatActionEntry) {
		this.firingFrameChats.add(entry.chatId);
		store.dispatch(markFrameChatActionFiring({ id: entry.id }));
		try {
			const resp = await forge.addFrameChatUserEntry.withToast()({
				frameId: entry.payload.frameId,
				entry: entry.payload.entry,
				selectedDomElements: entry.payload.selectedDomElements,
			});
			// First message on a new frame creates the chat — update the active id.
			if (resp?.frameChatId) {
				store.dispatch(setActiveFrameChatId(resp.frameChatId));
			}
			this.pendingFrameAcks.set(entry.chatId, {
				entryId: entry.id,
				chatId: entry.chatId,
				firedAt: Date.now(),
			});
			setTimeout(() => this.tick(), ACK_TIMEOUT_MS + 50);
		} catch (error) {
			// see fireDs comment
			Sentry.captureException(error);
			this.firingFrameChats.delete(entry.chatId);
			store.dispatch(popFrameChatAction({ id: entry.id }));
		}
	}
}
